import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { NGXLogger } from "ngx-logger";
import { defer, distinctUntilChanged } from "rxjs";
import {
  ConfirmationDetails,
  ConfirmationService,
} from "../../confirmation-dialog/confirmation.service";
import { SharedTableColumn } from "../../shared/components/shared-table/shared-table.component";
import {
  AddMember,
  RemoveMember,
  SetMembers,
  SetRoles,
} from "../state/members.actions";
import { Member } from "../state/members.reducer";
import { selectMembersState } from "../state/members.selector";
import { Workspace } from "../state/workspaces.model";
import { selectActiveWorkspace } from "../state/workspaces.selectors";
import {
  MemberRoleAssignmentError,
  WorkspaceMembersService,
} from "./workspace-members.service";

interface MemberRow extends Member {
  isOwner: boolean;
}

type OwnerFilter = "all" | "owner" | "member";

@Component({
  selector: "app-workspace-members",
  templateUrl: "./workspace-members.component.html",
  styleUrls: ["./workspace-members.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkspaceMembersComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly workspaceMembers = inject(WorkspaceMembersService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly logger = inject(NGXLogger);
  private readonly destroyRef = inject(DestroyRef);
  private workspaceVersion = 0;
  private membersRequest = 0;
  private rolesRequest = 0;

  readonly workspace = signal<Workspace | null>(null);
  readonly membersState = this.store.selectSignal(selectMembersState);
  readonly membersLoading = signal(false);
  readonly membersError = signal<string | null>(null);
  readonly rolesLoading = signal(false);
  readonly rolesError = signal<string | null>(null);
  readonly adding = signal(false);
  readonly addError = signal<string | null>(null);
  readonly roleRetry = signal<MemberRoleAssignmentError | null>(null);

  readonly email = new FormControl("", {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.email,
      (control) =>
        this.membersState().members.some(
          (member) =>
            member.email.toLowerCase() ===
            String(control.value).trim().toLowerCase(),
        )
          ? { memberExists: true }
          : null,
    ],
  });
  readonly form = new FormGroup({ email: this.email });
  readonly search = new FormControl("", { nonNullable: true });
  readonly ownerFilter = new FormControl<OwnerFilter>("all", {
    nonNullable: true,
  });
  private readonly searchText = toSignal(this.search.valueChanges, {
    initialValue: "",
  });
  private readonly ownerFilterValue = toSignal(this.ownerFilter.valueChanges, {
    initialValue: "all",
  });

  readonly columns: SharedTableColumn<MemberRow>[] = [
    { key: "email", header: "Email", sortable: true },
    { key: "isOwner", header: "Membership", sortable: true },
  ];
  readonly rows = computed<MemberRow[]>(() =>
    this.membersState().members.map((member) => ({
      ...member,
      isOwner: member.uuid === this.workspace()?.owner,
    })),
  );
  readonly filteredRows = computed(() => {
    const search = this.searchText().trim().toLowerCase();
    const owner = this.ownerFilterValue();
    return this.rows().filter((row) => {
      const ownerLabel = row.isOwner ? "workspace owner" : "member";
      return (
        (!search ||
          row.email.toLowerCase().includes(search) ||
          ownerLabel.includes(search)) &&
        (owner === "all" || row.isOwner === (owner === "owner"))
      );
    });
  });
  readonly hasFilters = computed(
    () => !!this.searchText() || this.ownerFilterValue() !== "all",
  );
  readonly emptyMessage = computed(() =>
    this.rows().length
      ? "No members match your search or filters."
      : "No members have been added to this workspace.",
  );
  readonly canAdd = computed(
    () =>
      !!this.workspace() &&
      !this.membersLoading() &&
      !this.membersError() &&
      !this.rolesLoading() &&
      !this.rolesError() &&
      this.membersState().roles.some((role) => role.name === "admin") &&
      !this.adding() &&
      !this.roleRetry(),
  );

  ngOnInit(): void {
    this.store
      .select(selectMembersState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.email.updateValueAndValidity({ emitEvent: false });
      });

    this.store
      .select(selectActiveWorkspace)
      .pipe(
        distinctUntilChanged(
          (previous, current) =>
            previous?.uuid === current?.uuid &&
            previous?.owner === current?.owner,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((workspace) => {
        this.workspaceVersion++;
        this.workspace.set(workspace ?? null);
        this.store.dispatch(new SetMembers([]));
        this.store.dispatch(new SetRoles([]));
        this.membersError.set(null);
        this.rolesError.set(null);
        this.membersLoading.set(false);
        this.rolesLoading.set(false);
        this.addError.set(null);
        this.adding.set(false);
        this.roleRetry.set(null);
        this.form.reset();
        this.clearFilters();
        if (workspace) {
          void this.loadMembers();
          void this.loadRoles();
        }
      });
  }

  async loadMembers(): Promise<void> {
    const version = this.workspaceVersion;
    const request = ++this.membersRequest;
    this.membersLoading.set(true);
    this.membersError.set(null);
    try {
      const members = await this.workspaceMembers.getWorkspaceMembers();
      if (this.isCurrent(version) && request === this.membersRequest) {
        this.store.dispatch(new SetMembers(members));
      }
    } catch (error: unknown) {
      this.logger.error("WORKSPACE MEMBERS: failed to load members", error);
      if (this.isCurrent(version) && request === this.membersRequest) {
        this.membersError.set("Unable to load workspace members.");
      }
    } finally {
      if (this.isCurrent(version) && request === this.membersRequest) {
        this.membersLoading.set(false);
      }
    }
  }

  async loadRoles(): Promise<void> {
    const version = this.workspaceVersion;
    const request = ++this.rolesRequest;
    this.rolesLoading.set(true);
    this.rolesError.set(null);
    try {
      const roles = await this.workspaceMembers.getWorkspaceRoles();
      if (!roles.some((role) => role.name === "admin")) {
        throw new Error("The workspace admin role is unavailable.");
      }
      if (this.isCurrent(version) && request === this.rolesRequest) {
        this.store.dispatch(new SetRoles(roles));
      }
    } catch (error: unknown) {
      this.logger.error("WORKSPACE MEMBERS: failed to load roles", error);
      if (this.isCurrent(version) && request === this.rolesRequest) {
        this.rolesError.set(
          "Unable to load the admin role required to add members.",
        );
      }
    } finally {
      if (this.isCurrent(version) && request === this.rolesRequest) {
        this.rolesLoading.set(false);
      }
    }
  }

  async addMember(): Promise<void> {
    this.email.updateValueAndValidity();
    if (!this.canAdd() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const workspace = this.workspace();
    const role = this.membersState().roles.find(
      (role) => role.name === "admin",
    );
    const version = this.workspaceVersion;
    const email = this.email.value.trim();
    this.adding.set(true);
    this.addError.set(null);
    try {
      const member = await this.workspaceMembers.addUserToWorkspaceByEmail(
        workspace.uuid,
        role.uuid,
        email,
      );
      if (this.isCurrent(version)) {
        this.store.dispatch(new AddMember(member));
        this.form.reset();
      }
    } catch (error: unknown) {
      this.logger.error("WORKSPACE MEMBERS: failed to add member", error);
      if (this.isCurrent(version)) {
        if (error instanceof MemberRoleAssignmentError) {
          this.roleRetry.set(error);
          this.addError.set(
            "The member was added, but the admin role could not be assigned.",
          );
          await this.loadMembers();
        } else {
          this.addError.set("Unable to add this member. Please try again.");
        }
      }
    } finally {
      if (this.isCurrent(version)) this.adding.set(false);
    }
  }

  async retryRoleAssignment(): Promise<void> {
    const retry = this.roleRetry();
    if (!retry || this.adding()) return;
    const version = this.workspaceVersion;
    this.adding.set(true);
    try {
      await this.workspaceMembers.assignRole(retry.member.uuid, retry.roleID);
      if (this.isCurrent(version)) {
        this.roleRetry.set(null);
        this.addError.set(null);
        this.form.reset();
      }
    } catch (error: unknown) {
      this.logger.error(
        "WORKSPACE MEMBERS: failed to assign admin role",
        error,
      );
      if (this.isCurrent(version)) {
        this.addError.set("Unable to assign the admin role. Please try again.");
      }
    } finally {
      if (this.isCurrent(version)) this.adding.set(false);
    }
  }

  removeMember(member: Member): void {
    const workspace = this.workspace();
    if (!workspace || workspace.owner === member.uuid) {
      this.logger.warn("WORKSPACE MEMBERS: owner removal is not allowed");
      return;
    }
    const version = this.workspaceVersion;
    const details = new ConfirmationDetails();
    details.title = `Remove "${member.email}" from this workspace?`;
    details.message = "This removes workspace access, not the user's account.";
    details.buttonLabel = "Remove";
    details.destructiveButton = true;
    details.pendingLabel = "Removing member...";
    details.failureMessage = "Unable to remove this member. Please try again.";
    details.onConfirmError = (error) =>
      this.logger.error("WORKSPACE MEMBERS: failed to remove member", error);
    details.onConfirmAsync = () =>
      defer(async () => {
        if (!this.isCurrent(version)) {
          throw new Error("The active workspace has changed.");
        }
        if (this.workspace()?.owner === member.uuid) {
          throw new Error("The workspace owner cannot be removed.");
        }
        await this.workspaceMembers.removeUserFromWorkspace(
          workspace.uuid,
          member.uuid,
        );
        if (this.isCurrent(version)) {
          this.store.dispatch(new RemoveMember(member.uuid));
          if (this.roleRetry()?.member.uuid === member.uuid) {
            this.roleRetry.set(null);
            this.addError.set(null);
          }
        }
      });
    this.confirmation.confirm(details);
  }

  clearFilters(): void {
    this.search.reset();
    this.ownerFilter.reset();
  }

  getErrorMessage(): string {
    if (this.email.hasError("required")) return "Required";
    if (this.email.hasError("email")) return "Enter a valid email address";
    if (this.email.hasError("memberExists")) return "Already a member";
    return "";
  }

  private isCurrent(version: number): boolean {
    return !this.destroyRef.destroyed && version === this.workspaceVersion;
  }
}
